import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  BRANCH_VOUCHER_REPORT_TYPE_KEYS,
  BRANCH_VOUCHER_REPORT_TYPES,
  buildBranchVoucherMonthwiseRows,
} from "./branchVoucherReportService";

const COMPANY_NAME = "WINS AUTOS";
const AMOUNT_FMT = "#,##0.00";
const INT_FMT = "#,##0";
const DATE_FMT = "dd-mmm-yyyy";

function exportFileName(bundle, suffix = "report") {
  const year = bundle?.meta?.year || "report";
  const branch = String(bundle?.meta?.branchId || "branch").replace(/\s+/g, "-");
  const brands = bundle?.meta?.selectedBrands?.length
    ? `-${String(bundle.meta.brandLabel).replace(/\s+/g, "-").replace(/\+/g, "and")}`
    : "";
  return `Branch-Voucher-${suffix}-${year}-${branch}${brands}.xlsx`;
}

function generatedAtLabel() {
  return new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function setCell(ws, r, c, value, { z, t } = {}) {
  const addr = XLSX.utils.encode_cell({ r, c });
  if (value == null || value === "") {
    ws[addr] = { t: "s", v: "" };
    return;
  }
  if (typeof value === "number") {
    ws[addr] = { t: "n", v: value };
    if (z) ws[addr].z = z;
    return;
  }
  if (value instanceof Date) {
    ws[addr] = { t: "d", v: value, z: z || DATE_FMT };
    return;
  }
  ws[addr] = { t: t || "s", v: String(value) };
  if (z) ws[addr].z = z;
}

function finalizeSheet(ws, { merges, cols, freezeRow = 0, freezeCol = 0 } = {}) {
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1:A1");
  if (merges?.length) ws["!merges"] = merges;
  if (cols?.length) ws["!cols"] = cols;
  if (freezeRow > 0 || freezeCol > 0) {
    ws["!views"] = [
      {
        state: "frozen",
        ySplit: freezeRow,
        xSplit: freezeCol,
        topLeftCell: XLSX.utils.encode_cell({ r: freezeRow, c: freezeCol }),
        activePane: "bottomRight",
      },
    ];
  }
  return ws;
}

function applyColumnFormats(ws, startRow, columnFormats) {
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1:A1");
  for (let r = startRow; r <= range.e.r; r += 1) {
    for (const { col, z } of columnFormats) {
      const addr = XLSX.utils.encode_cell({ r, c: col });
      const cell = ws[addr];
      if (cell && cell.t === "n" && z) cell.z = z;
    }
  }
}

function buildCoverSheet(bundle) {
  const meta = bundle.meta || {};
  const monthwiseRows = buildBranchVoucherMonthwiseRows(bundle);
  const topLevelRows = monthwiseRows.filter((r) => !r.indent);

  const aoa = [
    [COMPANY_NAME],
    ["Branch Voucher Reports"],
    [`Month-wise Summary — ${meta.year}`],
    [],
    ["Report Information"],
    ["Year", meta.year],
    ["Branch", meta.branchLabel],
    ["Brand filter", meta.brandLabel || "All brands"],
    ["Period", `01 Jan ${meta.year} — 31 Dec ${meta.year}`],
    ["Generated", generatedAtLabel()],
    [],
    ["Year Totals by Report Type"],
    ["Report Type", "Amount (₹)", "Invoices", "Line Items"],
  ];

  for (const row of topLevelRows) {
    aoa.push([
      row.label,
      row.report.summary.totalAmount,
      row.report.summary.voucherCount,
      row.report.summary.lineCount,
    ]);
  }

  const grandAmount = topLevelRows.reduce((s, r) => s + (r.report.summary.totalAmount || 0), 0);
  const grandInv = topLevelRows.reduce((s, r) => s + (r.report.summary.voucherCount || 0), 0);
  const grandLines = topLevelRows.reduce((s, r) => s + (r.report.summary.lineCount || 0), 0);
  aoa.push(["Grand Total (all report types)", grandAmount, grandInv, grandLines]);

  aoa.push([]);
  aoa.push(["Notes"]);
  aoa.push(["Amounts use import line Amount column; voucher totals use resolveImportVoucherTotal logic."]);
  aoa.push(["Outward Supply Br and Credit Sales Br are combined in report type 1."]);
  aoa.push(["Brand filter uses BrandName column from sales import line chunks."]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 42 }, { wch: 18 }, { wch: 12 }, { wch: 12 }];

  const amountRowsStart = aoa.findIndex((r) => r[0] === "Report Type") + 1;
  applyColumnFormats(ws, amountRowsStart, [
    { col: 1, z: AMOUNT_FMT },
    { col: 2, z: INT_FMT },
    { col: 3, z: INT_FMT },
  ]);

  return ws;
}

function buildMonthwiseGridSheet(bundle) {
  const meta = bundle.meta || {};
  const monthSlots = meta.months || [];
  const monthwiseRows = buildBranchVoucherMonthwiseRows(bundle);

  const headerRow1 = ["Report Type"];
  const headerRow2 = [""];
  const amountCols = [];
  const intCols = [];

  let col = 1;
  for (const slot of monthSlots) {
    headerRow1.push(slot.shortLabel, "");
    headerRow2.push("Amount (₹)", "Invoices");
    amountCols.push(col);
    intCols.push(col + 1);
    col += 2;
  }
  headerRow1.push("Year Total", "");
  headerRow2.push("Amount (₹)", "Invoices");
  amountCols.push(col);
  intCols.push(col + 1);
  const lastCol = col + 1;

  const titleRow = 0;
  const metaRow = 1;
  const headerStart = 3;
  const dataStart = 5;

  const ws = {};
  setCell(ws, titleRow, 0, `${COMPANY_NAME} — Branch Voucher Month-wise Summary ${meta.year}`);
  setCell(ws, metaRow, 0, `Branch: ${meta.branchLabel}  |  Brands: ${meta.brandLabel || "All brands"}  |  Generated: ${generatedAtLabel()}`);

  for (let c = 0; c <= lastCol; c += 1) {
    setCell(ws, headerStart, c, headerRow1[c] ?? "");
    setCell(ws, headerStart + 1, c, headerRow2[c] ?? "");
  }

  let r = dataStart;
  for (const row of monthwiseRows) {
    const label = row.indent ? `    ${row.label}` : row.label;
    setCell(ws, r, 0, label);

    let c = 1;
    for (const month of row.report.months || []) {
      setCell(ws, r, c, month.amount, { z: AMOUNT_FMT });
      setCell(ws, r, c + 1, month.voucherCount, { z: INT_FMT });
      c += 2;
    }
    setCell(ws, r, c, row.report.summary.totalAmount, { z: AMOUNT_FMT });
    setCell(ws, r, c + 1, row.report.summary.voucherCount, { z: INT_FMT });
    r += 1;
  }

  const ref = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: r - 1, c: lastCol } });
  ws["!ref"] = ref;

  const cols = [{ wch: 36 }];
  for (let i = 0; i < monthSlots.length + 1; i += 1) {
    cols.push({ wch: 14 }, { wch: 10 });
  }

  finalizeSheet(ws, {
    merges: [
      { s: { r: titleRow, c: 0 }, e: { r: titleRow, c: lastCol } },
      { s: { r: metaRow, c: 0 }, e: { r: metaRow, c: lastCol } },
      ...monthSlots.map((_, i) => ({
        s: { r: headerStart, c: 1 + i * 2 },
        e: { r: headerStart, c: 2 + i * 2 },
      })),
      { s: { r: headerStart, c: lastCol - 1 }, e: { r: headerStart, c: lastCol } },
    ],
    cols,
    freezeRow: dataStart,
    freezeCol: 1,
  });

  return ws;
}

function buildLongFormatSheet(bundle) {
  const meta = bundle.meta || {};
  const rows = [["Brand Filter", "Report Type", "Month", "Amount (₹)", "Invoices", "Line Items"]];
  const monthwiseRows = buildBranchVoucherMonthwiseRows(bundle);

  for (const row of monthwiseRows) {
    for (const month of row.report.months || []) {
      rows.push([
        meta.brandLabel || "All brands",
        row.label,
        month.label,
        month.amount,
        month.voucherCount,
        month.lineCount,
      ]);
    }
    rows.push([
      meta.brandLabel || "All brands",
      row.label,
      "Year Total",
      row.report.summary.totalAmount,
      row.report.summary.voucherCount,
      row.report.summary.lineCount,
    ]);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 22 }, { wch: 36 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 12 }];
  applyColumnFormats(ws, 1, [
    { col: 3, z: AMOUNT_FMT },
    { col: 4, z: INT_FMT },
    { col: 5, z: INT_FMT },
  ]);
  finalizeSheet(ws, { freezeRow: 1, freezeCol: 0 });
  return ws;
}

function buildVoucherDetailSheet(bundle) {
  const meta = bundle.meta || {};
  const header = [
    "Report Type",
    "Voucher No",
    "Date",
    "Month",
    "Voucher Type",
    "Customer",
    "Employee",
    "Line Items",
    "Amount (₹)",
  ];
  const rows = [header];

  for (const key of BRANCH_VOUCHER_REPORT_TYPE_KEYS) {
    const config = BRANCH_VOUCHER_REPORT_TYPES[key];
    const appendVouchers = (label, report) => {
      for (const v of report?.vouchers || []) {
        rows.push([
          label,
          v.voucherNo,
          v.invoiceDate,
          v.monthKey,
          v.voucherType,
          v.customerName,
          v.employeeName,
          v.lineCount,
          v.amount,
        ]);
      }
    };

    const report = bundle.reports[key];
    appendVouchers(config.listLabel, report);
    if (report?.subReports) {
      appendVouchers(report.subReports.outward.listLabel, report.subReports.outward);
      appendVouchers(report.subReports.credit.listLabel, report.subReports.credit);
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 34 },
    { wch: 14 },
    { wch: 12 },
    { wch: 10 },
    { wch: 22 },
    { wch: 24 },
    { wch: 18 },
    { wch: 10 },
    { wch: 14 },
  ];
  applyColumnFormats(ws, 1, [
    { col: 7, z: INT_FMT },
    { col: 8, z: AMOUNT_FMT },
  ]);
  finalizeSheet(ws, { freezeRow: 1, freezeCol: 0 });

  const noteRow = rows.length + 2;
  setCell(ws, noteRow, 0, `Brand filter: ${meta.brandLabel || "All brands"}`);
  ws["!ref"] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: noteRow, c: header.length - 1 },
  });

  return ws;
}

/**
 * Professional multi-sheet Excel export for branch voucher month-wise report.
 */
export function exportBranchVoucherMonthwiseExcel(bundle, fileName) {
  if (!bundle?.reports) return;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildCoverSheet(bundle), "Cover");
  XLSX.utils.book_append_sheet(wb, buildMonthwiseGridSheet(bundle), "Monthwise Grid");
  XLSX.utils.book_append_sheet(wb, buildLongFormatSheet(bundle), "By Month (Rows)");
  XLSX.utils.book_append_sheet(wb, buildVoucherDetailSheet(bundle), "Voucher Details");

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellDates: true });
  saveAs(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    fileName || exportFileName(bundle, "Monthwise")
  );
}

/**
 * Professional Excel export for filtered voucher list view.
 */
export function exportBranchVoucherVouchersExcel({
  bundle,
  activeReport,
  activeLabel,
  monthKey,
  monthLabel,
  fileName,
}) {
  if (!activeReport?.vouchers?.length) return;

  const meta = bundle?.meta || {};
  let vouchers = activeReport.vouchers;
  if (monthKey && monthKey !== "all") {
    vouchers = vouchers.filter((v) => v.monthKey === monthKey);
  }

  const cover = [
    [COMPANY_NAME],
    ["Branch Voucher — Voucher List"],
    [],
    ["Report Type", activeLabel || "—"],
    ["Year", meta.year],
    ["Branch", meta.branchLabel],
    ["Brand filter", meta.brandLabel || "All brands"],
    ["Month filter", monthLabel || "All months"],
    ["Generated", generatedAtLabel()],
    ["Voucher count", vouchers.length],
    [],
  ];

  const header = [
    "Voucher No",
    "Date",
    "Month",
    "Voucher Type",
    "Customer",
    "Employee",
    "Line Items",
    "Amount (₹)",
  ];
  const dataRows = vouchers.map((v) => [
    v.voucherNo,
    v.invoiceDate,
    v.monthKey,
    v.voucherType,
    v.customerName,
    v.employeeName,
    v.lineCount,
    v.amount,
  ]);

  const totalAmount = vouchers.reduce((s, v) => s + v.amount, 0);
  const totalLines = vouchers.reduce((s, v) => s + v.lineCount, 0);
  dataRows.push([]);
  dataRows.push(["TOTAL", "", "", "", "", "", totalLines, totalAmount]);

  const ws = XLSX.utils.aoa_to_sheet([...cover, header, ...dataRows]);
  ws["!cols"] = [
    { wch: 14 },
    { wch: 12 },
    { wch: 10 },
    { wch: 22 },
    { wch: 26 },
    { wch: 18 },
    { wch: 10 },
    { wch: 14 },
  ];

  const dataStart = cover.length + 1;
  applyColumnFormats(ws, dataStart, [
    { col: 6, z: INT_FMT },
    { col: 7, z: AMOUNT_FMT },
  ]);
  finalizeSheet(ws, { freezeRow: dataStart, freezeCol: 0 });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vouchers");

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array", cellDates: true });
  saveAs(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    fileName || exportFileName(bundle, "Vouchers")
  );
}
